import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Col, Row } from "reactstrap";
import ButtonComponent from "../../components/ButtonComponent";
import InputComponent from "../../components/InputComponent";
import Layout from "../../components/Layout/Layout";
import PageTitle from "../../components/Layout/PageTitle";
import QuotationPartsTable from "../../components/Quotation/QuotationPartsTable";
import SearchSelectComponent, { SelectItemsProps } from "../../components/SearchSelectComponent";
import { useAuth } from "../../context/AuthContext";
import RoutesObject from "../../helpers/Routes";
import Toast from "../../helpers/Toast";
import { Utilities } from "../../helpers/Utilities";
import { SearchByClientName } from "../../models/Client/SearchByClientName";
import { Parameter } from "../../models/Parameter";
import { CreateClientQuotationDTO } from "../../models/Quotation/CreateClientQuotationDTO";
import { PartQuotationListDTO } from "../../models/Quotation/PartQuotationListDTO";
import { PartsAndServicesQuotationDTO } from "../../models/Quotation/PartsAndServicesQuotationDTO";
import { SupplierQuotationDTO } from "../../models/Quotation/SupplierQuotationDTO";
import { AssistanceService } from "../../services/AssistanceService";
import { ClientQuotationService } from "../../services/ClientQuotationService";
import { ClientService } from "../../services/ClientService";
import { SupplierQuotationService } from "../../services/SupplierQuotationService";
import { UpdateClientQuotationDTO } from "../../models/Quotation/UpdateClientQuotationDTO";

export default function CreateClientQuotation() {
  const { userRoles } = useAuth();
  const intl = useIntl();
  const navigate = useNavigate();
  const clientService = new ClientService();
  const assistanceService = new AssistanceService();
  const clientQuotationService = new ClientQuotationService();
  const supplierQuotationService = new SupplierQuotationService();

  const [loading, setLoading] = useState(false);
  const [selectedClient, setSelectedClient] = useState<SelectItemsProps | null>(null);
  const [selectedAssistance, setSelectedAssistance] = useState<SelectItemsProps | null>(null);
  const [selectedSupplierQuotation, setSelectedSupplierQuotation] = useState<SelectItemsProps | null>(null);
  const [inputClient, setInputClient] = useState<string>("");
  const [inputAssistance, setInputAssistance] = useState<number | null>(null);
  const [inputSupplierQuotation, setInputSupplierQuotation] = useState<string>("");
  const [inputNotes, setInputNotes] = useState<string>("");
  const [inputDescription, setInputDescription] = useState<string>("");
  const [costPrice, setCostPrice] = useState<number>(0);
  const [totalValue, setTotalValue] = useState<number>(0);
  const [parts, setParts] = useState<PartQuotationListDTO[]>([]);

  const [searchParams] = useSearchParams();
  const assistanceQuery = searchParams.get("assistance");
  const clientQuery = searchParams.get("client");
  const [IsTabelVisible, setIsTableVisible] = useState<boolean>(false);
  const [quotationCreated, setQuotationCreated] = useState<boolean>(false);
  const [createdQuotationId, setCreatedQuotationId] = useState<number | null>(null);

  const canViewIndex = () => {
    if (!userRoles) return false;
    return userRoles.includes("Admin") || userRoles.includes("Técnico Escritório");
  };

  useEffect(() => {
    if (assistanceQuery) {
      const id = Number(assistanceQuery);
      setInputAssistance(id);
      if (clientQuery) {
        const clientId = (clientQuery);
        setInputClient(clientId.toString());
        clientService.GetByIdErp(clientId).then(resp => {
          if (resp.success && resp.obj) {
            const c = resp.obj;
            setSelectedClient({
              value: c.erpId.toString(),
              label: c.name
            });
          } else {
            Toast.Show("error", resp.message);
          }
        });
      }

      // Carrega dados da assistência para teres o label
      assistanceService.GetById(id).then(resp => {
        if (resp.success && resp.obj) {
          const a = resp.obj;
          setSelectedAssistance({
            value: a.id.toString(),
            label: `${a.erpDocType} ${a.erpSerie} ${a.erpNumDoc}`
          });
        } else {
          Toast.Show("error", resp.message);
        }
      });
    }
  }, []);

  const calculateTotalValue = (parts: PartQuotationListDTO[]) => {
    setParts(parts);

    let sum = 0;
    parts.forEach(part => {
      sum += Utilities.calculateTotalPrice(part.quantity, part.sellingPrice);
    });
    return sum;
  };

  const calculateCostValue = (parts: PartQuotationListDTO[]) => {
    setParts(parts);

    let sum = 0;
    parts.forEach(part => {
      sum += Utilities.calculateTotalPrice(part.quantity, part.costPrice);
    });
    return sum;
  };

const handleSaveParts = async () => {
  if (!quotationCreated || !createdQuotationId) {
    Toast.Show("error", "Primeiro crie a proposta antes de salvar as peças");
    return;
  }

  const newTotalValue = calculateTotalValue(parts);
  const newCostPrice = calculateCostValue(parts);
  
  await updateQuotationParts(parts, newTotalValue, newCostPrice);
  
  setTotalValue(newTotalValue);
  setCostPrice(newCostPrice);
};

const handlePartsChange = async (parts: PartQuotationListDTO[]) => {
  setParts(parts);
  const newTotalValue = calculateTotalValue(parts);
  const newCostPrice = calculateCostValue(parts);
  setTotalValue(newTotalValue);
  setCostPrice(newCostPrice);

 
};

  const handleSubmit = async () => {
    
    if (!selectedClient) {
      Toast.Show("error", intl.formatMessage({ id: "Screens.Quotation.Parts.Info.ClientMissing" }));
      return;
    }
    const currentTotalValue = calculateTotalValue(parts);
    const currentCostPrice = calculateCostValue(parts);

   

    if (inputDescription.length > 500) {
      Toast.Show("error", intl.formatMessage({ id: "Screens.Quotation.Info.DescriptionMaxLength" }));
      return;
    }

    setLoading(true);

     if (quotationCreated) {
      if (canViewIndex()) {
        if (assistanceQuery != null) {
          navigate(`${RoutesObject.backoffice.details}/${assistanceQuery}`);
        } else {
          navigate(`${RoutesObject.clientQuotations.list}`);
        }
        } else {
        navigate(`${RoutesObject.oficina.details}/${assistanceQuery}`);
        }
      setLoading(false);
      return;
    }
    const partsAndServicesQuotationDTO: PartsAndServicesQuotationDTO[] = [];
    parts.forEach(part => {
      const partAndServices: PartsAndServicesQuotationDTO = {
        partName: part.name,
        partId: part.code,
        quantity: part.quantity,
        sellingPrice: part.sellingPrice,
        costPrice: part.costPrice,
        totalSellingPrice: Utilities.calculateTotalPrice(part.quantity, part.sellingPrice)
      };
      partsAndServicesQuotationDTO.push(partAndServices);
    });

    const clientQuotationDTO: CreateClientQuotationDTO = {
      clientErpId: inputClient.toString(),
      clientId: parseInt(inputClient),
      assistanceId: inputAssistance ? parseInt(inputAssistance.toString()) : undefined,
      supplierQuotationId: parseInt(inputSupplierQuotation),
      description: inputDescription,
      notes: inputNotes,
      partsAndServicesQuotationDTO: partsAndServicesQuotationDTO,
      sellingPrice: currentTotalValue,
      costPrice: currentCostPrice
    };

    const response = await clientQuotationService.Create(clientQuotationDTO);

    if (response.success === false || response.obj === null) {
      Toast.Show("error", response.message);
      setLoading(false);
      return;
    }

    setCreatedQuotationId(response.obj || response.obj);
    setQuotationCreated(true);
    setIsTableVisible(true);
    
    Toast.Show("success", intl.formatMessage({ id: "Screens.ClientQuotation.Parts.Info.Success" }));
    setLoading(false);
  };
const updateQuotationParts = async (
  parts: PartQuotationListDTO[],
  totalValue: number,
  costPrice: number
) => {
  if (!createdQuotationId) {
    return;
  }

  try {
    setLoading(true);

    const partsAndServicesQuotationDTO: PartsAndServicesQuotationDTO[] = parts.map(part => ({
      partName: part.name,
      partId: String(part.code),
      quantity: Number(part.quantity),
      sellingPrice: Number(part.sellingPrice),
      costPrice: Number(part.costPrice),
      totalSellingPrice: Utilities.calculateTotalPrice(part.quantity, part.sellingPrice)
    }));

    const updateQuotationDTO: UpdateClientQuotationDTO = {
      quotationId: createdQuotationId,
      clientId: parseInt(inputClient),
      description: inputDescription,
      partsAndServicesQuotationDTO: partsAndServicesQuotationDTO,
      sellingPrice: totalValue,
      costPrice: costPrice
    };


    const response = await clientQuotationService.Update(updateQuotationDTO, createdQuotationId);

    if (!response.success) {
      Toast.Show("error", response.message || "Erro ao atualizar a proposta");
    } else {
     
      Toast.Show("success", intl.formatMessage({ id: "Guardado com sucesso" }));
    }
  } catch (error) {
    Toast.Show("error", "Erro ao atualizar a proposta");
  } finally {
    setLoading(false);
  }
};


  const searchClient = async (
    name: string
  ): Promise<SelectItemsProps[]> => {
    const searchParams: SearchByClientName = { name };

    const response = await clientService.SearchByName(searchParams);

    if (response.success === false || response.obj === null) {
      Toast.Show("error", response.message);
    }

    const list: SelectItemsProps[] = [];
    response.obj?.forEach((element) => {
      const item: SelectItemsProps = {
        value: element.erpId,
        label: element.name,
      };
      list.push(item);
    });

    return list;
  };


  const searchAssistance = async (
    docNumber: string
  ): Promise<SelectItemsProps[]> => {
    if (inputClient.toString() === "") {
      Toast.Show("error", intl.formatMessage({ id: "Screens.ClientQuotation.Create.ChooseAssistance.WithoutClient" }));
      return [];
    }
    const parameters: Parameter[] = [
      new Parameter('numDoc', docNumber),
      new Parameter('clientErpId', inputClient.toString())
    ];

    const response = await assistanceService.GetAll(parameters, [], 1, 10);


    if (response.success === false || !response.items) {
      Toast.Show("error", response.message);
    }

    const list: SelectItemsProps[] = [];
    response.items?.forEach((element) => {
      const item: SelectItemsProps = {
        value: element.assistanceId?.toString() ?? "",
        label: element.docType + " " + element.serie.toString() + " " + element.number.toString(),
      };
      list.push(item);
    });
    return list;
  };

  const searchSupplierQuotation = async (
    id: number
  ) => {

    setLoading(true);

    //Vamos obter os dados
    var parameters: Parameter[] = [];
    parameters.push(new Parameter("id", id));

    var response = await supplierQuotationService.GetAll(
      parameters, parameters, 1, 5);

    if (response.success === false || response === null) {
      Toast.Show("error", response.message);
      setLoading(false);
      return [];
    }

    const list: SelectItemsProps[] = [];
    if (response.items) {
      response.items.forEach((element: SupplierQuotationDTO) => {
        const item: SelectItemsProps = {
          value: element.id.toString(),
          label: element.id.toString(),
        };
        list.push(item);
      });
    }

    setLoading(false);
    return list;
  };

  return (
    <Layout>
      <div className="pageContainer">
        <div className="pageHeader">
          <PageTitle title={"Nova Proposta"} allowGoBack={true} />
        </div>
        <div className="pageBody">
          <label style={{ margin: "0px", padding: "13px" }}>
            <b>{intl.formatMessage({ id: "Screens.Quotation.Info" })}</b>
          </label>
          <Row style={{ margin: "0px", padding: "8px" }}>
            <SearchSelectComponent
              xl={1.5}
              lg={2}
              md={4}
              sm={8}
              xs={12}
              label={intl.formatMessage({
                id: "Screens.Quotation.Info.Client",
              })}
              required={true}
              loadOptions={searchClient}
              onChange={(e: SelectItemsProps) => {
                setSelectedClient(e);
                setInputClient(e.value);
                setSelectedAssistance(null);
                setInputAssistance(null);
              }}
              value={selectedClient}
              isDisabled={clientQuery !== null || quotationCreated}
              style={{ width: "100%" }}
            />

            <SearchSelectComponent
              xl={1.5}
              lg={2}
              md={4}
              sm={8}
              xs={12}
              label={intl.formatMessage({
                id: "Screens.Quotation.Info.Assistance",
              })}
              loadOptions={searchAssistance}
              onChange={(e: SelectItemsProps) => {
                setSelectedAssistance(e);
                setInputAssistance(Number(e.value));
              }}
              value={selectedAssistance}
              isDisabled={!selectedClient || assistanceQuery !== null || quotationCreated}
              style={{ width: "100%" }}
            />
            <SearchSelectComponent
              xl={1.5}
              lg={2}
              md={4}
              sm={8}
              xs={12}
              label={intl.formatMessage({
                id: "Screens.Quotation.Info.SupplierQuotation",
              })}
              loadOptions={(value: string) => searchSupplierQuotation(Number(value))}
              onChange={(e: SelectItemsProps) => {
                setSelectedSupplierQuotation(e);
                setInputSupplierQuotation(e.value);
              }}
              value={selectedSupplierQuotation}
              isDisabled={quotationCreated}
              style={{ width: "100%" }}
            />
          </Row>

          <Row style={{ margin: "0px", padding: "8px" }}>
            <Col>
              <InputComponent
                xl={5}
                lg={5}
                md={5}
                sm={10}
                xs={10}
                style={{ width: "119%" }}
                type={"textArea"}
                value={inputDescription}
                label={intl.formatMessage({ id: "Screens.Quotation.Info.Description" })}
                readOnly={quotationCreated}
                required={true}
                onChange={(t: string) => setInputDescription(t)}
                placeholder={intl.formatMessage({ id: "Screens.Quotation.Info.MaxLength" })}
              />
            </Col>
          </Row>
          
          {IsTabelVisible && (
            <div>
              <Row style={{ margin: "0px", padding: "8px" }}>
                <QuotationPartsTable 
                  initialParts={parts ?? []} 
                  reloadParts={false} 
                  goToPage={0} 
                  currentPage={0} 
                  isComplete={false} 
                  
                  onPartsChange={handlePartsChange} 
                  quotationType="ClientQuotation" 
                  
                  quotationId={createdQuotationId ?? 0}
                  clientId={selectedClient?.value}
                  description={inputDescription}
                  
                />
              </Row>
              <Row style={{ margin: "0px", padding: "8px" }}>
                <InputComponent
                  xl={6}
                  lg={8}
                  md={10}
                  sm={12}
                  xs={12}
                  style={{ width: "100%" }}
                  type={"text"}
                  value={inputNotes}
                  label={intl.formatMessage({ id: "Screens.Quotation.Info.Notes" })}
                  readOnly={true}
                  onChange={(t: string) => setInputNotes(t)}
                />
                <InputComponent
                  xl={2}
                  lg={4}
                  md={5}
                  sm={12}
                  xs={12}
                  style={{ width: "100%" }}
                  type={"number"}
                  value={totalValue}
                  label={intl.formatMessage({ id: "Screens.Quotation.Parts.Label.TotalValue" })}
                  readOnly={true}
                />
                
              </Row>
              
            </div>
          )}
          <Row style={{ margin: "0px", padding: "8px", justifyContent: "flex-start" }}>
            <Col xs={6}>
              <ButtonComponent
                disabled={loading}
                loading={loading}
                text={intl.formatMessage({ id: "Screens.ClientQuotation.Button.Create" })}
                onClick={() => {
                  if (IsTabelVisible){
                    handleSaveParts();
                  }
                  
                  handleSubmit();
                }}
                style={{ width: "25%" }}
              />
            </Col>
            
          </Row>
        </div>
      </div>
    </Layout>
  );
}